import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, Stack, TextField, Switch, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import apiClient from '../../api';

export default function ApiKeysManagement() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newOrigins, setNewOrigins] = useState('');
  const [issued, setIssued] = useState(null); // plaintext result
  const [newMode, setNewMode] = useState('public');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/apikeys');
      setKeys(res.data.data.keys || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createKey = async () => {
    const allowedOrigins = newOrigins.split(',').map(s => s.trim()).filter(Boolean);
    const res = await apiClient.post('/apikeys', { label: newLabel, allowedOrigins, mode: newMode });
    setIssued(res.data.data.plaintext);
    setOpen(false);
    setNewLabel('');
    setNewOrigins('');
    setNewMode('public');
    load();
  };

  const toggleActive = async (id, active) => {
    await apiClient.patch(`/apikeys/${id}`, { active });
    load();
  };

  const updateOrigins = async (id, allowedOrigins) => {
    await apiClient.patch(`/apikeys/${id}`, { allowedOrigins });
    load();
  };

  const revoke = async (id) => {
    await apiClient.delete(`/apikeys/${id}`);
    load();
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">API Keys</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Create API Key</Button>
      </Stack>
      {issued && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="error">Copy this key now; it will not be shown again.</Typography>
          <Paper sx={{ p: 1, mt: 1 }}><code>{issued}</code></Paper>
        </Box>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Label</TableCell>
            <TableCell>Allowed Origins</TableCell>
            <TableCell>Scopes</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map(k => (
            <TableRow key={k._id}>
              <TableCell>{k.label}</TableCell>
              <TableCell>
                <TextField size="small" defaultValue={(k.allowedOrigins||[]).join(', ')} onBlur={(e) => updateOrigins(k._id, e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} fullWidth />
              </TableCell>
              <TableCell>{(k.scopes||[]).map(s => <Chip key={s} size="small" label={s} sx={{ mr: .5 }} />)}</TableCell>
              <TableCell><Switch checked={!!k.active} onChange={(e)=>toggleActive(k._id, e.target.checked)} /></TableCell>
              <TableCell>{new Date(k.createdAt).toLocaleString()}</TableCell>
              <TableCell align="right">
                <Button color="error" size="small" onClick={() => revoke(k._id)}>Revoke</Button>
              </TableCell>
            </TableRow>
          ))}
          {keys.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>No API keys yet.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Label" value={newLabel} onChange={(e)=>setNewLabel(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel id="mode-label">Mode</InputLabel>
              <Select labelId="mode-label" label="Mode" value={newMode} onChange={(e)=>setNewMode(e.target.value)}>
                <MenuItem value="public">Public (client-side)</MenuItem>
                <MenuItem value="secret">Secret (server-side)</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Allowed Origins (comma-separated)" value={newOrigins} onChange={(e)=>setNewOrigins(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createKey} disabled={!newLabel}>Create</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}


